import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconRegistry } from '@angular/material/icon';
import { DialogResult } from '../../models/enums/dialog-result/dialog.result';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { CommuneTO, CompanyTO } from '../../../api/generated/v2/incident';
import { CommuneService } from 'app/client-settings/services/commune.service';
import { CompanyDataModalImpl } from '../companies/company-data-modal.impl';
import { ActionsModalOperationType } from '../../models/enums/company/actions-modal-operation-type';
import { CompanyDataService } from '../../services/company-data.service';

@Component({
    selector: 'app-create-company-modal',
    templateUrl: './company-modal.component.html',
    styleUrls: ['./company-modal.component.scss'],
    standalone: false
})
export class CompanyModalComponent implements OnInit {

  public createCompanyForm: UntypedFormGroup = new UntypedFormGroup({
    companyName: new UntypedFormControl('', [Validators.required, Validators.maxLength(100)]),
    city: new UntypedFormControl('', [Validators.required, Validators.maxLength(100)]),
    zipCode: new UntypedFormControl('', [Validators.required, Validators.maxLength(10)]),
    address: new UntypedFormControl('', [Validators.required, Validators.maxLength(100)]),
  });

  communeCache: CommuneTO[] = [];

  autocompletedCityOptions$: Observable<CommuneTO[]> = this.createCompanyForm
    .get('city').valueChanges
    .pipe(
      debounceTime(300),
      map((city: string) => {
        if (!city?.trim()) { return []; }
        return this.communeCache.filter(c =>
          c.town?.toLowerCase().startsWith(city.trim().toLowerCase())
        );
      }),
    );

  constructor(
    public dialogRef: MatDialogRef<CompanyModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CompanyDataModalImpl,
    protected sanitizer: DomSanitizer,
    private readonly communeService: CommuneService,
    private readonly companyDataService: CompanyDataService,
    protected iconRegistry: MatIconRegistry,
  ) {
    iconRegistry.addSvgIcon('qm', sanitizer.bypassSecurityTrustResourceUrl('/assets/icons/question-mark.svg'));
  }

  ngOnInit(): void {
    this.communeService.getAllCommunes().subscribe((communes: CommuneTO[]) => {
      this.communeCache = communes;
    });

    if (this.data.operation === ActionsModalOperationType.UPDATE) {
      this.createCompanyForm.patchValue({
        companyName: this.data.companyName,
        city: this.data.city,
        zipCode: this.data.zipCode,
        address: this.data.address,
      });
    }
  }

  public onClsBtnClick(): void {
    this.close(DialogResult.CLOSED);
  }

  public onRevokeBtnClick(): void {
    this.close(DialogResult.NO);
  }

  public onConfirmBtnClick(): void {
    if (this.data.operation === ActionsModalOperationType.CREATE) {
      this.createCompany();
    } else if (this.data.operation === ActionsModalOperationType.UPDATE) {
      this.updateCompany();
    }
  }

  private updateCompany() {
    const updateCompany: CompanyTO = {
      companyName: this.createCompanyForm.get('companyName').value,
      city: this.createCompanyForm.get('city').value,
      zipCode: this.createCompanyForm.get('zipCode').value,
      address: this.createCompanyForm.get('address').value,
    };

    this.companyDataService.updateCompany(this.data.uuid, updateCompany).subscribe((response: any) => {
      const isSuccess: boolean = 200 === response.status;
      this.close(isSuccess ? DialogResult.SUCCESS : DialogResult.FAIL);
    }, (error: any) => {
      this.close(DialogResult.FAIL);
    });
  }

  private createCompany(): void {
    const data: CompanyTO = {
      companyName: this.createCompanyForm.get('companyName').value,
      city: this.createCompanyForm.get('city').value,
      zipCode: this.createCompanyForm.get('zipCode').value,
      address: this.createCompanyForm.get('address').value,
    };

    this.companyDataService.createCompany(data).subscribe((response: any) => {
      const isSuccess: boolean = 200 === response.status;
      this.close(isSuccess ? DialogResult.SUCCESS : DialogResult.FAIL);
    }, (error: any) => {
      this.close(DialogResult.FAIL);
    });
  }

  private close(res: DialogResult): void {
    this.dialogRef.close(res);
  }

  public onCommuneSelect(commune: CommuneTO | null) {
    if (commune == null) {
      this.createCompanyForm.get('city').setErrors({ incorrect: true });
    } else {
      this.createCompanyForm.get('city').patchValue(commune?.town);
      this.createCompanyForm.get('zipCode').patchValue(commune?.zipCode);
    }
  }
}
